/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `workout_step` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class WorkoutStepMesg : Mesg {
    public constructor() : super("WorkoutStep", Profile.MesgNum.WORKOUT_STEP)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** wkt_step_name. */
    public var wktStepName: String?
        get() = getFieldValue(WKT_STEP_NAME_FIELD_NUM) as? String
        set(value) = setFieldValue(WKT_STEP_NAME_FIELD_NUM, value)

    /** duration_type. */
    public var durationType: WktStepDuration?
        get() = (getFieldValue(DURATION_TYPE_FIELD_NUM) as? UByte)?.let { WktStepDuration.fromValue(it) }
        set(value) = setFieldValue(DURATION_TYPE_FIELD_NUM, value?.value)

    /** duration_value. */
    public var durationValue: UInt?
        get() = getFieldValue(DURATION_VALUE_FIELD_NUM) as? UInt
        set(value) = setFieldValue(DURATION_VALUE_FIELD_NUM, value)

    /**
     * duration_time: what duration_value means when this message selects it.
     */
    public val durationTime: Double?
        get() = getFieldValue(DURATION_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("DurationTime")) as? Double

    public fun setDurationTime(value: Double?) {
        setFieldValue(DURATION_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("DurationTime"))
    }

    /**
     * duration_distance: what duration_value means when this message selects it.
     */
    public val durationDistance: Double?
        get() = getFieldValue(DURATION_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("DurationDistance")) as? Double

    public fun setDurationDistance(value: Double?) {
        setFieldValue(DURATION_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("DurationDistance"))
    }

    /**
     * duration_hr: what duration_value means when this message selects it.
     */
    public val durationHr: UInt?
        get() = getFieldValue(DURATION_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("DurationHr")) as? UInt

    public fun setDurationHr(value: UInt?) {
        setFieldValue(DURATION_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("DurationHr"))
    }

    /**
     * duration_calories: what duration_value means when this message selects it.
     */
    public val durationCalories: UInt?
        get() = getFieldValue(DURATION_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("DurationCalories")) as? UInt

    public fun setDurationCalories(value: UInt?) {
        setFieldValue(DURATION_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("DurationCalories"))
    }

    /**
     * duration_step: what duration_value means when this message selects it.
     *
     * message_index of step to loop back to. Steps are assumed to be in the order by message_index. custom_name and intensity members are undefined for this duration type.
     */
    public val durationStep: UInt?
        get() = getFieldValue(DURATION_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("DurationStep")) as? UInt

    public fun setDurationStep(value: UInt?) {
        setFieldValue(DURATION_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("DurationStep"))
    }

    /**
     * duration_power: what duration_value means when this message selects it.
     */
    public val durationPower: UInt?
        get() = getFieldValue(DURATION_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("DurationPower")) as? UInt

    public fun setDurationPower(value: UInt?) {
        setFieldValue(DURATION_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("DurationPower"))
    }

    /**
     * duration_reps: what duration_value means when this message selects it.
     */
    public val durationReps: UInt?
        get() = getFieldValue(DURATION_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("DurationReps")) as? UInt

    public fun setDurationReps(value: UInt?) {
        setFieldValue(DURATION_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("DurationReps"))
    }

    /** target_type. */
    public var targetType: WktStepTarget?
        get() = (getFieldValue(TARGET_TYPE_FIELD_NUM) as? UByte)?.let { WktStepTarget.fromValue(it) }
        set(value) = setFieldValue(TARGET_TYPE_FIELD_NUM, value?.value)

    /** target_value. */
    public var targetValue: UInt?
        get() = getFieldValue(TARGET_VALUE_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TARGET_VALUE_FIELD_NUM, value)

    /**
     * target_speed_zone: what target_value means when this message selects it.
     *
     * speed zone (1-10);Custom =0;
     */
    public val targetSpeedZone: UInt?
        get() = getFieldValue(TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("TargetSpeedZone")) as? UInt

    public fun setTargetSpeedZone(value: UInt?) {
        setFieldValue(TARGET_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("TargetSpeedZone"))
    }

    /**
     * target_hr_zone: what target_value means when this message selects it.
     *
     * hr zone (1-5);Custom =0;
     */
    public val targetHrZone: UInt?
        get() = getFieldValue(TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("TargetHrZone")) as? UInt

    public fun setTargetHrZone(value: UInt?) {
        setFieldValue(TARGET_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("TargetHrZone"))
    }

    /**
     * target_cadence_zone: what target_value means when this message selects it.
     *
     * Zone (1-?); Custom = 0;
     */
    public val targetCadenceZone: UInt?
        get() = getFieldValue(TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("TargetCadenceZone")) as? UInt

    public fun setTargetCadenceZone(value: UInt?) {
        setFieldValue(TARGET_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("TargetCadenceZone"))
    }

    /**
     * target_power_zone: what target_value means when this message selects it.
     *
     * Power Zone ( 1-7); Custom = 0;
     */
    public val targetPowerZone: UInt?
        get() = getFieldValue(TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("TargetPowerZone")) as? UInt

    public fun setTargetPowerZone(value: UInt?) {
        setFieldValue(TARGET_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("TargetPowerZone"))
    }

    /**
     * repeat_steps: what target_value means when this message selects it.
     *
     * # of repetitions
     */
    public val repeatSteps: UInt?
        get() = getFieldValue(TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("RepeatSteps")) as? UInt

    public fun setRepeatSteps(value: UInt?) {
        setFieldValue(TARGET_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("RepeatSteps"))
    }

    /**
     * repeat_time: what target_value means when this message selects it.
     */
    public val repeatTime: Double?
        get() = getFieldValue(TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("RepeatTime")) as? Double

    public fun setRepeatTime(value: Double?) {
        setFieldValue(TARGET_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("RepeatTime"))
    }

    /**
     * repeat_distance: what target_value means when this message selects it.
     */
    public val repeatDistance: Double?
        get() = getFieldValue(TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("RepeatDistance")) as? Double

    public fun setRepeatDistance(value: Double?) {
        setFieldValue(TARGET_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("RepeatDistance"))
    }

    /**
     * repeat_calories: what target_value means when this message selects it.
     */
    public val repeatCalories: UInt?
        get() = getFieldValue(TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("RepeatCalories")) as? UInt

    public fun setRepeatCalories(value: UInt?) {
        setFieldValue(TARGET_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("RepeatCalories"))
    }

    /**
     * repeat_hr: what target_value means when this message selects it.
     */
    public val repeatHr: UInt?
        get() = getFieldValue(TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("RepeatHr")) as? UInt

    public fun setRepeatHr(value: UInt?) {
        setFieldValue(TARGET_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("RepeatHr"))
    }

    /**
     * repeat_power: what target_value means when this message selects it.
     */
    public val repeatPower: UInt?
        get() = getFieldValue(TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("RepeatPower")) as? UInt

    public fun setRepeatPower(value: UInt?) {
        setFieldValue(TARGET_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("RepeatPower"))
    }

    /**
     * target_stroke_type: what target_value means when this message selects it.
     */
    public val targetStrokeType: SwimStroke?
        get() = (getFieldValue(TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("TargetStrokeType")) as? UByte)?.let { SwimStroke.fromValue(it) }

    public fun setTargetStrokeType(value: SwimStroke?) {
        setFieldValue(TARGET_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("TargetStrokeType"))
    }

    /** custom_target_value_low. */
    public var customTargetValueLow: UInt?
        get() = getFieldValue(CUSTOM_TARGET_VALUE_LOW_FIELD_NUM) as? UInt
        set(value) = setFieldValue(CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, value)

    /**
     * custom_target_speed_low: what custom_target_value_low means when this message selects it.
     */
    public val customTargetSpeedLow: Double?
        get() = getFieldValue(CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, 0, SubFieldSelector.Named("CustomTargetSpeedLow")) as? Double

    public fun setCustomTargetSpeedLow(value: Double?) {
        setFieldValue(CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, value, 0, SubFieldSelector.Named("CustomTargetSpeedLow"))
    }

    /**
     * custom_target_heart_rate_low: what custom_target_value_low means when this message selects it.
     */
    public val customTargetHeartRateLow: UInt?
        get() = getFieldValue(CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, 0, SubFieldSelector.Named("CustomTargetHeartRateLow")) as? UInt

    public fun setCustomTargetHeartRateLow(value: UInt?) {
        setFieldValue(CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, value, 0, SubFieldSelector.Named("CustomTargetHeartRateLow"))
    }

    /**
     * custom_target_cadence_low: what custom_target_value_low means when this message selects it.
     */
    public val customTargetCadenceLow: UInt?
        get() = getFieldValue(CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, 0, SubFieldSelector.Named("CustomTargetCadenceLow")) as? UInt

    public fun setCustomTargetCadenceLow(value: UInt?) {
        setFieldValue(CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, value, 0, SubFieldSelector.Named("CustomTargetCadenceLow"))
    }

    /**
     * custom_target_power_low: what custom_target_value_low means when this message selects it.
     */
    public val customTargetPowerLow: UInt?
        get() = getFieldValue(CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, 0, SubFieldSelector.Named("CustomTargetPowerLow")) as? UInt

    public fun setCustomTargetPowerLow(value: UInt?) {
        setFieldValue(CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, value, 0, SubFieldSelector.Named("CustomTargetPowerLow"))
    }

    /** custom_target_value_high. */
    public var customTargetValueHigh: UInt?
        get() = getFieldValue(CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM) as? UInt
        set(value) = setFieldValue(CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, value)

    /**
     * custom_target_speed_high: what custom_target_value_high means when this message selects it.
     */
    public val customTargetSpeedHigh: Double?
        get() = getFieldValue(CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, 0, SubFieldSelector.Named("CustomTargetSpeedHigh")) as? Double

    public fun setCustomTargetSpeedHigh(value: Double?) {
        setFieldValue(CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, value, 0, SubFieldSelector.Named("CustomTargetSpeedHigh"))
    }

    /**
     * custom_target_heart_rate_high: what custom_target_value_high means when this message selects it.
     */
    public val customTargetHeartRateHigh: UInt?
        get() = getFieldValue(CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, 0, SubFieldSelector.Named("CustomTargetHeartRateHigh")) as? UInt

    public fun setCustomTargetHeartRateHigh(value: UInt?) {
        setFieldValue(CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, value, 0, SubFieldSelector.Named("CustomTargetHeartRateHigh"))
    }

    /**
     * custom_target_cadence_high: what custom_target_value_high means when this message selects it.
     */
    public val customTargetCadenceHigh: UInt?
        get() = getFieldValue(CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, 0, SubFieldSelector.Named("CustomTargetCadenceHigh")) as? UInt

    public fun setCustomTargetCadenceHigh(value: UInt?) {
        setFieldValue(CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, value, 0, SubFieldSelector.Named("CustomTargetCadenceHigh"))
    }

    /**
     * custom_target_power_high: what custom_target_value_high means when this message selects it.
     */
    public val customTargetPowerHigh: UInt?
        get() = getFieldValue(CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, 0, SubFieldSelector.Named("CustomTargetPowerHigh")) as? UInt

    public fun setCustomTargetPowerHigh(value: UInt?) {
        setFieldValue(CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, value, 0, SubFieldSelector.Named("CustomTargetPowerHigh"))
    }

    /** intensity. */
    public var intensity: Intensity?
        get() = (getFieldValue(INTENSITY_FIELD_NUM) as? UByte)?.let { Intensity.fromValue(it) }
        set(value) = setFieldValue(INTENSITY_FIELD_NUM, value?.value)

    /** notes. */
    public var notes: String?
        get() = getFieldValue(NOTES_FIELD_NUM) as? String
        set(value) = setFieldValue(NOTES_FIELD_NUM, value)

    /** equipment. */
    public var equipment: WorkoutEquipment?
        get() = (getFieldValue(EQUIPMENT_FIELD_NUM) as? UByte)?.let { WorkoutEquipment.fromValue(it) }
        set(value) = setFieldValue(EQUIPMENT_FIELD_NUM, value?.value)

    /** exercise_category. */
    public var exerciseCategory: ExerciseCategory?
        get() = (getFieldValue(EXERCISE_CATEGORY_FIELD_NUM) as? UShort)?.let { ExerciseCategory.fromValue(it) }
        set(value) = setFieldValue(EXERCISE_CATEGORY_FIELD_NUM, value?.value)

    /** exercise_name. */
    public var exerciseName: UShort?
        get() = getFieldValue(EXERCISE_NAME_FIELD_NUM) as? UShort
        set(value) = setFieldValue(EXERCISE_NAME_FIELD_NUM, value)

    /** exercise_weight, in kg. */
    public var exerciseWeight: Double?
        get() = getFieldValue(EXERCISE_WEIGHT_FIELD_NUM) as? Double
        set(value) = setFieldValue(EXERCISE_WEIGHT_FIELD_NUM, value)

    /** weight_display_unit. */
    public var weightDisplayUnit: FitBaseUnit?
        get() = (getFieldValue(WEIGHT_DISPLAY_UNIT_FIELD_NUM) as? UShort)?.let { FitBaseUnit.fromValue(it) }
        set(value) = setFieldValue(WEIGHT_DISPLAY_UNIT_FIELD_NUM, value?.value)

    /** secondary_target_type. */
    public var secondaryTargetType: WktStepTarget?
        get() = (getFieldValue(SECONDARY_TARGET_TYPE_FIELD_NUM) as? UByte)?.let { WktStepTarget.fromValue(it) }
        set(value) = setFieldValue(SECONDARY_TARGET_TYPE_FIELD_NUM, value?.value)

    /** secondary_target_value. */
    public var secondaryTargetValue: UInt?
        get() = getFieldValue(SECONDARY_TARGET_VALUE_FIELD_NUM) as? UInt
        set(value) = setFieldValue(SECONDARY_TARGET_VALUE_FIELD_NUM, value)

    /**
     * secondary_target_speed_zone: what secondary_target_value means when this message selects it.
     *
     * speed zone (1-10);Custom =0;
     */
    public val secondaryTargetSpeedZone: UInt?
        get() = getFieldValue(SECONDARY_TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("SecondaryTargetSpeedZone")) as? UInt

    public fun setSecondaryTargetSpeedZone(value: UInt?) {
        setFieldValue(SECONDARY_TARGET_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("SecondaryTargetSpeedZone"))
    }

    /**
     * secondary_target_hr_zone: what secondary_target_value means when this message selects it.
     *
     * hr zone (1-5);Custom =0;
     */
    public val secondaryTargetHrZone: UInt?
        get() = getFieldValue(SECONDARY_TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("SecondaryTargetHrZone")) as? UInt

    public fun setSecondaryTargetHrZone(value: UInt?) {
        setFieldValue(SECONDARY_TARGET_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("SecondaryTargetHrZone"))
    }

    /**
     * secondary_target_cadence_zone: what secondary_target_value means when this message selects it.
     *
     * Zone (1-?); Custom = 0;
     */
    public val secondaryTargetCadenceZone: UInt?
        get() = getFieldValue(SECONDARY_TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("SecondaryTargetCadenceZone")) as? UInt

    public fun setSecondaryTargetCadenceZone(value: UInt?) {
        setFieldValue(SECONDARY_TARGET_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("SecondaryTargetCadenceZone"))
    }

    /**
     * secondary_target_power_zone: what secondary_target_value means when this message selects it.
     *
     * Power Zone ( 1-7); Custom = 0;
     */
    public val secondaryTargetPowerZone: UInt?
        get() = getFieldValue(SECONDARY_TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("SecondaryTargetPowerZone")) as? UInt

    public fun setSecondaryTargetPowerZone(value: UInt?) {
        setFieldValue(SECONDARY_TARGET_VALUE_FIELD_NUM, value, 0, SubFieldSelector.Named("SecondaryTargetPowerZone"))
    }

    /**
     * secondary_target_stroke_type: what secondary_target_value means when this message selects it.
     */
    public val secondaryTargetStrokeType: SwimStroke?
        get() = (getFieldValue(SECONDARY_TARGET_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("SecondaryTargetStrokeType")) as? UByte)?.let { SwimStroke.fromValue(it) }

    public fun setSecondaryTargetStrokeType(value: SwimStroke?) {
        setFieldValue(SECONDARY_TARGET_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("SecondaryTargetStrokeType"))
    }

    /** secondary_custom_target_value_low. */
    public var secondaryCustomTargetValueLow: UInt?
        get() = getFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_LOW_FIELD_NUM) as? UInt
        set(value) = setFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, value)

    /**
     * secondary_custom_target_speed_low: what secondary_custom_target_value_low means when this message selects it.
     */
    public val secondaryCustomTargetSpeedLow: Double?
        get() = getFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, 0, SubFieldSelector.Named("SecondaryCustomTargetSpeedLow")) as? Double

    public fun setSecondaryCustomTargetSpeedLow(value: Double?) {
        setFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, value, 0, SubFieldSelector.Named("SecondaryCustomTargetSpeedLow"))
    }

    /**
     * secondary_custom_target_heart_rate_low: what secondary_custom_target_value_low means when this message selects it.
     */
    public val secondaryCustomTargetHeartRateLow: UInt?
        get() = getFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, 0, SubFieldSelector.Named("SecondaryCustomTargetHeartRateLow")) as? UInt

    public fun setSecondaryCustomTargetHeartRateLow(value: UInt?) {
        setFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, value, 0, SubFieldSelector.Named("SecondaryCustomTargetHeartRateLow"))
    }

    /**
     * secondary_custom_target_cadence_low: what secondary_custom_target_value_low means when this message selects it.
     */
    public val secondaryCustomTargetCadenceLow: UInt?
        get() = getFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, 0, SubFieldSelector.Named("SecondaryCustomTargetCadenceLow")) as? UInt

    public fun setSecondaryCustomTargetCadenceLow(value: UInt?) {
        setFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, value, 0, SubFieldSelector.Named("SecondaryCustomTargetCadenceLow"))
    }

    /**
     * secondary_custom_target_power_low: what secondary_custom_target_value_low means when this message selects it.
     */
    public val secondaryCustomTargetPowerLow: UInt?
        get() = getFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, 0, SubFieldSelector.Named("SecondaryCustomTargetPowerLow")) as? UInt

    public fun setSecondaryCustomTargetPowerLow(value: UInt?) {
        setFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_LOW_FIELD_NUM, value, 0, SubFieldSelector.Named("SecondaryCustomTargetPowerLow"))
    }

    /** secondary_custom_target_value_high. */
    public var secondaryCustomTargetValueHigh: UInt?
        get() = getFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM) as? UInt
        set(value) = setFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, value)

    /**
     * secondary_custom_target_speed_high: what secondary_custom_target_value_high means when this message selects it.
     */
    public val secondaryCustomTargetSpeedHigh: Double?
        get() = getFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, 0, SubFieldSelector.Named("SecondaryCustomTargetSpeedHigh")) as? Double

    public fun setSecondaryCustomTargetSpeedHigh(value: Double?) {
        setFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, value, 0, SubFieldSelector.Named("SecondaryCustomTargetSpeedHigh"))
    }

    /**
     * secondary_custom_target_heart_rate_high: what secondary_custom_target_value_high means when this message selects it.
     */
    public val secondaryCustomTargetHeartRateHigh: UInt?
        get() = getFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, 0, SubFieldSelector.Named("SecondaryCustomTargetHeartRateHigh")) as? UInt

    public fun setSecondaryCustomTargetHeartRateHigh(value: UInt?) {
        setFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, value, 0, SubFieldSelector.Named("SecondaryCustomTargetHeartRateHigh"))
    }

    /**
     * secondary_custom_target_cadence_high: what secondary_custom_target_value_high means when this message selects it.
     */
    public val secondaryCustomTargetCadenceHigh: UInt?
        get() = getFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, 0, SubFieldSelector.Named("SecondaryCustomTargetCadenceHigh")) as? UInt

    public fun setSecondaryCustomTargetCadenceHigh(value: UInt?) {
        setFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, value, 0, SubFieldSelector.Named("SecondaryCustomTargetCadenceHigh"))
    }

    /**
     * secondary_custom_target_power_high: what secondary_custom_target_value_high means when this message selects it.
     */
    public val secondaryCustomTargetPowerHigh: UInt?
        get() = getFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, 0, SubFieldSelector.Named("SecondaryCustomTargetPowerHigh")) as? UInt

    public fun setSecondaryCustomTargetPowerHigh(value: UInt?) {
        setFieldValue(SECONDARY_CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM, value, 0, SubFieldSelector.Named("SecondaryCustomTargetPowerHigh"))
    }

    public companion object {
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val WKT_STEP_NAME_FIELD_NUM: UByte = 0u
        public const val DURATION_TYPE_FIELD_NUM: UByte = 1u
        public const val DURATION_VALUE_FIELD_NUM: UByte = 2u
        public const val TARGET_TYPE_FIELD_NUM: UByte = 3u
        public const val TARGET_VALUE_FIELD_NUM: UByte = 4u
        public const val CUSTOM_TARGET_VALUE_LOW_FIELD_NUM: UByte = 5u
        public const val CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM: UByte = 6u
        public const val INTENSITY_FIELD_NUM: UByte = 7u
        public const val NOTES_FIELD_NUM: UByte = 8u
        public const val EQUIPMENT_FIELD_NUM: UByte = 9u
        public const val EXERCISE_CATEGORY_FIELD_NUM: UByte = 10u
        public const val EXERCISE_NAME_FIELD_NUM: UByte = 11u
        public const val EXERCISE_WEIGHT_FIELD_NUM: UByte = 12u
        public const val WEIGHT_DISPLAY_UNIT_FIELD_NUM: UByte = 13u
        public const val SECONDARY_TARGET_TYPE_FIELD_NUM: UByte = 19u
        public const val SECONDARY_TARGET_VALUE_FIELD_NUM: UByte = 20u
        public const val SECONDARY_CUSTOM_TARGET_VALUE_LOW_FIELD_NUM: UByte = 21u
        public const val SECONDARY_CUSTOM_TARGET_VALUE_HIGH_FIELD_NUM: UByte = 22u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[0] = Field("WktStepName", 0u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[1] = Field("DurationType", 1u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[2] = Field("DurationValue", 2u, BaseType.UINT32, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("DurationTime", BaseType.UINT32, 1000.0, 0.0, "s").also { subField ->
                        subField.addMap(1u, 0)
                        subField.addMap(1u, 28)
                    },
                )
                .addSubField(
                    SubField("DurationDistance", BaseType.UINT32, 100.0, 0.0, "m").also { subField ->
                        subField.addMap(1u, 1)
                    },
                )
                .addSubField(
                    SubField("DurationHr", BaseType.UINT32, 1.0, 0.0, "% or bpm").also { subField ->
                        subField.addMap(1u, 2)
                        subField.addMap(1u, 3)
                    },
                )
                .addSubField(
                    SubField("DurationCalories", BaseType.UINT32, 1.0, 0.0, "calories").also { subField ->
                        subField.addMap(1u, 4)
                    },
                )
                .addSubField(
                    SubField("DurationStep", BaseType.UINT32, 1.0, 0.0, "").also { subField ->
                        subField.addMap(1u, 6)
                        subField.addMap(1u, 7)
                        subField.addMap(1u, 8)
                        subField.addMap(1u, 9)
                        subField.addMap(1u, 10)
                        subField.addMap(1u, 11)
                        subField.addMap(1u, 12)
                        subField.addMap(1u, 13)
                    },
                )
                .addSubField(
                    SubField("DurationPower", BaseType.UINT32, 1.0, 0.0, "% or watts").also { subField ->
                        subField.addMap(1u, 14)
                        subField.addMap(1u, 15)
                    },
                )
                .addSubField(
                    SubField("DurationReps", BaseType.UINT32, 1.0, 0.0, "").also { subField ->
                        subField.addMap(1u, 29)
                    },
                )
            fields[3] = Field("TargetType", 3u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[4] = Field("TargetValue", 4u, BaseType.UINT32, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("TargetSpeedZone", BaseType.UINT32, 1.0, 0.0, "").also { subField ->
                        subField.addMap(3u, 0)
                    },
                )
                .addSubField(
                    SubField("TargetHrZone", BaseType.UINT32, 1.0, 0.0, "").also { subField ->
                        subField.addMap(3u, 1)
                    },
                )
                .addSubField(
                    SubField("TargetCadenceZone", BaseType.UINT32, 1.0, 0.0, "").also { subField ->
                        subField.addMap(3u, 3)
                    },
                )
                .addSubField(
                    SubField("TargetPowerZone", BaseType.UINT32, 1.0, 0.0, "").also { subField ->
                        subField.addMap(3u, 4)
                    },
                )
                .addSubField(
                    SubField("RepeatSteps", BaseType.UINT32, 1.0, 0.0, "").also { subField ->
                        subField.addMap(1u, 6)
                    },
                )
                .addSubField(
                    SubField("RepeatTime", BaseType.UINT32, 1000.0, 0.0, "s").also { subField ->
                        subField.addMap(1u, 7)
                    },
                )
                .addSubField(
                    SubField("RepeatDistance", BaseType.UINT32, 100.0, 0.0, "m").also { subField ->
                        subField.addMap(1u, 8)
                    },
                )
                .addSubField(
                    SubField("RepeatCalories", BaseType.UINT32, 1.0, 0.0, "calories").also { subField ->
                        subField.addMap(1u, 9)
                    },
                )
                .addSubField(
                    SubField("RepeatHr", BaseType.UINT32, 1.0, 0.0, "% or bpm").also { subField ->
                        subField.addMap(1u, 10)
                        subField.addMap(1u, 11)
                    },
                )
                .addSubField(
                    SubField("RepeatPower", BaseType.UINT32, 1.0, 0.0, "% or watts").also { subField ->
                        subField.addMap(1u, 12)
                        subField.addMap(1u, 13)
                    },
                )
                .addSubField(
                    SubField("TargetStrokeType", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(3u, 11)
                    },
                )
            fields[5] = Field("CustomTargetValueLow", 5u, BaseType.UINT32, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("CustomTargetSpeedLow", BaseType.UINT32, 1000.0, 0.0, "m/s").also { subField ->
                        subField.addMap(3u, 0)
                    },
                )
                .addSubField(
                    SubField("CustomTargetHeartRateLow", BaseType.UINT32, 1.0, 0.0, "% or bpm").also { subField ->
                        subField.addMap(3u, 1)
                    },
                )
                .addSubField(
                    SubField("CustomTargetCadenceLow", BaseType.UINT32, 1.0, 0.0, "rpm").also { subField ->
                        subField.addMap(3u, 3)
                    },
                )
                .addSubField(
                    SubField("CustomTargetPowerLow", BaseType.UINT32, 1.0, 0.0, "% or watts").also { subField ->
                        subField.addMap(3u, 4)
                    },
                )
            fields[6] = Field("CustomTargetValueHigh", 6u, BaseType.UINT32, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("CustomTargetSpeedHigh", BaseType.UINT32, 1000.0, 0.0, "m/s").also { subField ->
                        subField.addMap(3u, 0)
                    },
                )
                .addSubField(
                    SubField("CustomTargetHeartRateHigh", BaseType.UINT32, 1.0, 0.0, "% or bpm").also { subField ->
                        subField.addMap(3u, 1)
                    },
                )
                .addSubField(
                    SubField("CustomTargetCadenceHigh", BaseType.UINT32, 1.0, 0.0, "rpm").also { subField ->
                        subField.addMap(3u, 3)
                    },
                )
                .addSubField(
                    SubField("CustomTargetPowerHigh", BaseType.UINT32, 1.0, 0.0, "% or watts").also { subField ->
                        subField.addMap(3u, 4)
                    },
                )
            fields[7] = Field("Intensity", 7u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[8] = Field("Notes", 8u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[9] = Field("Equipment", 9u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[10] = Field("ExerciseCategory", 10u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[11] = Field("ExerciseName", 11u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[12] = Field("ExerciseWeight", 12u, BaseType.UINT16, 100.0, 0.0, "kg", false)
            fields[13] = Field("WeightDisplayUnit", 13u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[19] = Field("SecondaryTargetType", 19u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[20] = Field("SecondaryTargetValue", 20u, BaseType.UINT32, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("SecondaryTargetSpeedZone", BaseType.UINT32, 1.0, 0.0, "").also { subField ->
                        subField.addMap(19u, 0)
                    },
                )
                .addSubField(
                    SubField("SecondaryTargetHrZone", BaseType.UINT32, 1.0, 0.0, "").also { subField ->
                        subField.addMap(19u, 1)
                    },
                )
                .addSubField(
                    SubField("SecondaryTargetCadenceZone", BaseType.UINT32, 1.0, 0.0, "").also { subField ->
                        subField.addMap(19u, 3)
                    },
                )
                .addSubField(
                    SubField("SecondaryTargetPowerZone", BaseType.UINT32, 1.0, 0.0, "").also { subField ->
                        subField.addMap(19u, 4)
                    },
                )
                .addSubField(
                    SubField("SecondaryTargetStrokeType", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(19u, 11)
                    },
                )
            fields[21] = Field("SecondaryCustomTargetValueLow", 21u, BaseType.UINT32, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("SecondaryCustomTargetSpeedLow", BaseType.UINT32, 1000.0, 0.0, "m/s").also { subField ->
                        subField.addMap(19u, 0)
                    },
                )
                .addSubField(
                    SubField("SecondaryCustomTargetHeartRateLow", BaseType.UINT32, 1.0, 0.0, "% or bpm").also { subField ->
                        subField.addMap(19u, 1)
                    },
                )
                .addSubField(
                    SubField("SecondaryCustomTargetCadenceLow", BaseType.UINT32, 1.0, 0.0, "rpm").also { subField ->
                        subField.addMap(19u, 3)
                    },
                )
                .addSubField(
                    SubField("SecondaryCustomTargetPowerLow", BaseType.UINT32, 1.0, 0.0, "% or watts").also { subField ->
                        subField.addMap(19u, 4)
                    },
                )
            fields[22] = Field("SecondaryCustomTargetValueHigh", 22u, BaseType.UINT32, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("SecondaryCustomTargetSpeedHigh", BaseType.UINT32, 1000.0, 0.0, "m/s").also { subField ->
                        subField.addMap(19u, 0)
                    },
                )
                .addSubField(
                    SubField("SecondaryCustomTargetHeartRateHigh", BaseType.UINT32, 1.0, 0.0, "% or bpm").also { subField ->
                        subField.addMap(19u, 1)
                    },
                )
                .addSubField(
                    SubField("SecondaryCustomTargetCadenceHigh", BaseType.UINT32, 1.0, 0.0, "rpm").also { subField ->
                        subField.addMap(19u, 3)
                    },
                )
                .addSubField(
                    SubField("SecondaryCustomTargetPowerHigh", BaseType.UINT32, 1.0, 0.0, "% or watts").also { subField ->
                        subField.addMap(19u, 4)
                    },
                )
            return fields
        }
    }
}
